import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

function formatTime(s) {
  if (!s) return "0m";
  if (s < 60) return `${s}s`;
  const mins = Math.round(s / 60);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

export default function Progress() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/progress").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-forge-900 text-forge-100">
      <Header />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-16 sm:py-24">
        <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-4">Your Growth</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-3" data-testid="progress-title">
          Intellectual growth, measured plainly.
        </h1>
        <p className="text-sm text-forge-100/60 mb-12 max-w-xl">
          Research or read, reflect, debate, write in the Journal, then return here to trace your growth.
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <Big label="Articles" value={data?.articles_completed ?? 0} testid="prog-articles" />
          <Big label="Reading Time" value={formatTime(data?.total_reading_seconds)} testid="prog-time" />
          <Big label="Vocabulary" value={data?.vocabulary_words_learned ?? 0} testid="prog-vocab" />
          <Big label="Reflections" value={data?.reflections_completed ?? 0} testid="prog-reflections" />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <Big label="Debate Sessions" value={data?.debate_sessions_completed ?? 0} testid="prog-debates" />
          <Big label="Challenges Met" value={data?.intellectual_challenges_completed ?? 0} testid="prog-challenges" />
          <Big label="Journal Entries" value={data?.journal_entries_completed ?? 0} testid="prog-journal" />
          <Big label="Research Sessions" value={data?.research_sessions_completed ?? 0} testid="prog-research" />
        </div>

        {/* Reasoning Pulse — average score + strongest/weakest categories */}
        {data?.report_cards_completed > 0 ? (
          <section className="mb-16 border border-white/10 rounded-sm p-6 sm:p-8" data-testid="reasoning-pulse">
            <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
              <div>
                <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-2">Reasoning Pulse</div>
                <p className="text-sm text-forge-100/55 max-w-xl">Aggregated across {data.report_cards_completed} report card{data.report_cards_completed === 1 ? "" : "s"}. Coaching, not grading.</p>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-[0.3em] text-forge-100/45 mb-1">Average</div>
                <div className="font-serif text-5xl text-ember-400" data-testid="reasoning-avg">{data.avg_reasoning_score ?? "—"}</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-forge-100/40 mt-1">out of 100</div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-px bg-white/5">
              {data.strongest_category && (
                <div className="bg-forge-900 p-5">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-ember-500 mb-2">Strongest Category</div>
                  <div className="font-serif text-2xl text-forge-100" data-testid="reasoning-strongest">{data.strongest_category.label}</div>
                  <div className="text-xs text-forge-100/55 mt-1">Avg {data.strongest_category.avg} / 10</div>
                </div>
              )}
              {data.weakest_category && (
                <div className="bg-forge-900 p-5">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-gold-500 mb-2">Growth Edge</div>
                  <div className="font-serif text-2xl text-forge-100" data-testid="reasoning-weakest">{data.weakest_category.label}</div>
                  <div className="text-xs text-forge-100/55 mt-1">Avg {data.weakest_category.avg} / 10 — invest your next debate here.</div>
                </div>
              )}
            </div>
          </section>
        ) : null}

        {data?.recent_debate_reports?.length ? (
          <section className="mb-16">
            <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-4">Debate Reports</div>
            <ul className="divide-y divide-white/5 border-t border-b border-white/5" data-testid="recent-report-list">
              {data.recent_debate_reports.map((report) => (
                <li key={report.debate_id} className="flex justify-between items-center py-4 flex-wrap gap-4">
                  <div>
                    <div className="font-serif text-lg text-forge-100">{report.reading_title}</div>
                    <div className="text-xs uppercase tracking-[0.25em] text-forge-100/40">
                      {report.completed_at ? report.completed_at.slice(0, 10) : "Completed"} · {report.reasoning_level || "Report Card"}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="font-serif text-3xl text-ember-400">{report.overall_score ?? "—"}</div>
                    <Link to={`/debate/${report.debate_id}/report`} className="text-xs uppercase tracking-[0.25em] px-4 py-2 border border-white/15 hover:border-ember-500 hover:text-ember-400 transition-all rounded-sm">
                      View Report
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* Growth Chart */}
        <div className="mb-16">
          <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-4">Reading Time Over Time</div>
          {data?.timeline?.length ? (
            <div className="h-56 border border-white/10 rounded-sm p-4 bg-forge-800/40" data-testid="growth-chart">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.timeline} margin={{ top: 10, right: 10, bottom: 0, left: -20 }}>
                  <defs>
                    <linearGradient id="emberFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#D97325" stopOpacity={0.55} />
                      <stop offset="100%" stopColor="#D97325" stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="2 4" stroke="rgba(242,239,233,0.06)" />
                  <XAxis dataKey="label" stroke="#75716D" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                  <YAxis stroke="#75716D" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} width={40} />
                  <Tooltip
                    contentStyle={{ background: "#181615", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "4px", color: "#F2EFE9", fontSize: 12 }}
                    labelStyle={{ color: "#D97325", textTransform: "uppercase", letterSpacing: "0.15em", fontSize: 10 }}
                    formatter={(v) => [`${v} min`, "Reading"]}
                  />
                  <Area type="monotone" dataKey="minutes" stroke="#D97325" strokeWidth={2} fill="url(#emberFill)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="border border-dashed border-white/10 rounded-sm p-8 text-center text-sm text-forge-100/40">
              Finish a reading to begin tracing your growth.
            </div>
          )}
        </div>

        <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-4">Recent</div>
        {data?.recent_sessions?.length ? (
          <ul className="divide-y divide-white/5 border-t border-b border-white/5" data-testid="recent-list">
            {data.recent_sessions.map((s) => (
              <li key={s.session_id} className="flex justify-between items-center py-4 flex-wrap gap-3">
                <Link to={`/reading/${s.session_id}`} className="font-serif text-lg text-forge-100 hover:text-ember-400 transition-colors">{s.title}</Link>
                <span className="text-xs uppercase tracking-[0.25em] text-forge-100/40">{formatTime(s.duration_seconds)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="border border-dashed border-white/10 rounded-sm p-10 text-center">
            <p className="text-sm text-forge-100/60 mb-4">No completed readings yet. The forge is cold.</p>
            <Link to="/learn" className="inline-block px-5 py-2.5 bg-ember-500 hover:bg-ember-400 text-forge-900 font-semibold rounded-sm tracking-wide transition-all" data-testid="prog-start-link">
              Start your first reading
            </Link>
          </div>
        )}

        <div className="mt-12 flex flex-wrap gap-4">
          {data?.journal_entries_completed > 0 ? (
            <Link to="/journal" className="text-xs uppercase tracking-[0.25em] px-4 py-2 border border-white/15 hover:border-gold-500 hover:text-gold-400 transition-all rounded-sm" data-testid="prog-journal-link">
              Open Journal →
            </Link>
          ) : (
            <Link to="/learn" className="text-xs uppercase tracking-[0.25em] px-4 py-2 border border-white/15 hover:border-ember-500 hover:text-ember-400 transition-all rounded-sm" data-testid="prog-journal-link">
              Start: Read → Reflect → Debate → Journal
            </Link>
          )}
        </div>
      </main>
    </div>
  );
}

function Big({ label, value, testid }) {
  return (
    <div className="border border-white/10 p-8 rounded-sm hover:border-ember-500/50 transition-colors" data-testid={testid}>
      <div className="text-[10px] tracking-[0.3em] uppercase text-forge-100/50 mb-3">{label}</div>
      <div className="font-serif text-5xl text-forge-100">{value}</div>
    </div>
  );
}
